import type { Vec2 } from '@/geometry/vec2';
import { boundingBox } from '@/geometry/polygon';

/**
 * 2D editor viewport. World y points up (crease-pattern convention); screen y
 * points down, so the transform flips it exactly once, here.
 */
export interface Viewport {
  scale: number;
  tx: number;
  ty: number;
  width: number;
  height: number;
}

export const worldToScreen = (vp: Viewport, p: Vec2): Vec2 => ({
  x: p.x * vp.scale + vp.tx,
  y: -p.y * vp.scale + vp.ty,
});

export const screenToWorld = (vp: Viewport, p: Vec2): Vec2 => ({
  x: (p.x - vp.tx) / vp.scale,
  y: -(p.y - vp.ty) / vp.scale,
});

/** Pixels per world unit -> world units per pixel. */
export const pxToWorld = (vp: Viewport, px: number): number => px / vp.scale;

export function fitViewport(
  points: readonly Vec2[],
  width: number,
  height: number,
  padding = 40,
): Viewport {
  if (points.length === 0 || width <= 0 || height <= 0) {
    return { scale: 1, tx: width / 2, ty: height / 2, width, height };
  }
  const bb = boundingBox(points);
  const w = Math.max(bb.width, 1e-6);
  const h = Math.max(bb.height, 1e-6);
  const scale = Math.min((width - padding * 2) / w, (height - padding * 2) / h);
  const cx = (bb.min.x + bb.max.x) / 2;
  const cy = (bb.min.y + bb.max.y) / 2;
  return {
    scale,
    tx: width / 2 - cx * scale,
    ty: height / 2 + cy * scale,
    width,
    height,
  };
}

export function zoomAt(vp: Viewport, screenPoint: Vec2, factor: number): Viewport {
  const before = screenToWorld(vp, screenPoint);
  const scale = Math.max(1e-3, Math.min(1e7, vp.scale * factor));
  const next = { ...vp, scale };
  const after = screenToWorld(next, screenPoint);
  return {
    ...next,
    tx: next.tx + (after.x - before.x) * scale,
    ty: next.ty - (after.y - before.y) * scale,
  };
}
